import React, { useEffect, useState } from 'react';
import BackgroundImage from '../CatcherThrowsFlow/images/CatcherThrows.png';
import { ISequence } from 'interfaces/tagging/ISequence';
import { IApiPlayerExtended } from '@trackman/baseball-shared-components';
import { DefensivePlayerPositions } from 'enums/DefensivePlayerPositions';
import { Hotkeys } from 'constants/Hotkeys';
import SequenceBottomBar from './components/SequenceBottomBar/SequenceBottomBar';
import ArrowSequence from './ArrowSequence';
import { FieldingSequencePlayer } from './components/FieldingSequencePlayer/FieldingSequencePlayer';
import { EditChip } from '../EditChip/EditChip';
import { FieldingSequenceFlowWrapper, FirstRow, FourthRow, SecondRow, ThirdRow } from './FieldingSequenceFlowStyles';

interface IProps {
  exitSequenceCallback: () => void;
  saveFieldingSequence: (sequence: ISequence[]) => void;
  onSequenceChange: (sequence: ISequence[]) => void;
  savedSequence: IApiPlayerExtended[];
  review: boolean;
}

const MAX_SEQUENCE_LENGTH = 3;

const FieldingSequenceFlow: React.FC<IProps> = (props) => {
  const { exitSequenceCallback, saveFieldingSequence, onSequenceChange, savedSequence, review } = props;
  const [sequence, setSequence] = useState<ISequence[]>([]);

  const addPlayerToSequence = (playerNumber: ISequence): void => {
    if (sequence.length < MAX_SEQUENCE_LENGTH && !sequence.includes(playerNumber)) {
      setSequence((prevSequence) => {
        return [...prevSequence, playerNumber];
      });
    }
  };

  useEffect(() => {
    const tempArr: ISequence[] = [];

    savedSequence.forEach((player) => {
      if (player) {
        tempArr.push(DefensivePlayerPositions[player.position]);
      }
    });
    setSequence(tempArr);
  }, [savedSequence]);

  const onSaveSequenceHandler = (): void => {
    saveFieldingSequence(sequence);
  };

  const onDeleteSequenceHandler = (): void => {
    setSequence((prevState) => prevState.slice(0, -1));
  };

  useEffect(() => {
    onSequenceChange(sequence);
  }, [sequence]);

  const isEditingMode = () => {
    return savedSequence[0] !== null;
  };

  return (
    <>
      <FieldingSequenceFlowWrapper
        className={`fielding-sequence-flow ${review ? 'review' : ''}`}
        style={{ backgroundImage: `url(${BackgroundImage})` }}
      >
        {isEditingMode() && <EditChip />}
        <ArrowSequence sequence={sequence} />
        <FirstRow>
          <FieldingSequencePlayer
            hotkeyCode={Hotkeys.FieldingSequence.LeftFielder.keyCode}
            hotkeyDisplay={Hotkeys.FieldingSequence.LeftFielder.hotkeyDisplay}
            onPressHotkeyCallback={addPlayerToSequence}
            onClickCallback={addPlayerToSequence}
            selected={sequence.includes('7') || sequence.includes(7)}
          />
          <FieldingSequencePlayer
            hotkeyCode={Hotkeys.FieldingSequence.CenterFielder.keyCode}
            hotkeyDisplay={Hotkeys.FieldingSequence.CenterFielder.hotkeyDisplay}
            onPressHotkeyCallback={addPlayerToSequence}
            onClickCallback={addPlayerToSequence}
            selected={sequence.includes('8') || sequence.includes(8)}
          />
          <FieldingSequencePlayer
            hotkeyCode={Hotkeys.FieldingSequence.RightFielder.keyCode}
            hotkeyDisplay={Hotkeys.FieldingSequence.RightFielder.hotkeyDisplay}
            onPressHotkeyCallback={addPlayerToSequence}
            onClickCallback={addPlayerToSequence}
            selected={sequence.includes('9') || sequence.includes(9)}
          />
        </FirstRow>
        <SecondRow>
          <FieldingSequencePlayer
            hotkeyCode={Hotkeys.FieldingSequence.ShortStopper.keyCode}
            hotkeyDisplay={Hotkeys.FieldingSequence.ShortStopper.hotkeyDisplay}
            onPressHotkeyCallback={addPlayerToSequence}
            onClickCallback={addPlayerToSequence}
            selected={sequence.includes('6') || sequence.includes(6)}
          />
          <FieldingSequencePlayer
            hotkeyCode={Hotkeys.FieldingSequence.SecondBaseman.keyCode}
            hotkeyDisplay={Hotkeys.FieldingSequence.SecondBaseman.hotkeyDisplay}
            onPressHotkeyCallback={addPlayerToSequence}
            onClickCallback={addPlayerToSequence}
            selected={sequence.includes('4') || sequence.includes(4)}
          />
        </SecondRow>
        <ThirdRow>
          <FieldingSequencePlayer
            hotkeyCode={Hotkeys.FieldingSequence.ThirdBaseman.keyCode}
            hotkeyDisplay={Hotkeys.FieldingSequence.ThirdBaseman.hotkeyDisplay}
            onPressHotkeyCallback={addPlayerToSequence}
            onClickCallback={addPlayerToSequence}
            selected={sequence.includes('5') || sequence.includes(5)}
          />
          <FieldingSequencePlayer
            hotkeyCode={Hotkeys.FieldingSequence.Pitcher.keyCode}
            hotkeyDisplay={Hotkeys.FieldingSequence.Pitcher.hotkeyDisplay}
            onPressHotkeyCallback={addPlayerToSequence}
            onClickCallback={addPlayerToSequence}
            selected={sequence.includes('1') || sequence.includes(1)}
          />
          <FieldingSequencePlayer
            hotkeyCode={Hotkeys.FieldingSequence.FirstBaseman.keyCode}
            hotkeyDisplay={Hotkeys.FieldingSequence.FirstBaseman.hotkeyDisplay}
            onPressHotkeyCallback={addPlayerToSequence}
            onClickCallback={addPlayerToSequence}
            selected={sequence.includes('3') || sequence.includes(3)}
          />
        </ThirdRow>
        <FourthRow>
          <FieldingSequencePlayer
            hotkeyCode={Hotkeys.FieldingSequence.Catcher.keyCode}
            hotkeyDisplay={Hotkeys.FieldingSequence.Catcher.hotkeyDisplay}
            onPressHotkeyCallback={addPlayerToSequence}
            onClickCallback={addPlayerToSequence}
            selected={sequence.includes('2') || sequence.includes(2)}
          />
        </FourthRow>
      </FieldingSequenceFlowWrapper>
      <SequenceBottomBar
        onExitCallback={exitSequenceCallback}
        onDeleteCallback={onDeleteSequenceHandler}
        onSaveCallback={onSaveSequenceHandler}
        sequence={sequence}
      />
    </>
  );
};

export default FieldingSequenceFlow;
